import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Form, Input, InputNumber, Radio, Switch, Button, Card, Row, Col, Select } from "antd";
import { handleApiSuccess, handleApiError } from "utils";
import {
  billingItemByIdService,
  billingItemCreateService,
  billingItemUpdateService,
  billingCategoryListService,
} from "services/billing";
import UnitInput from "../UnitInput";

const { Option } = Select;

export default function ItemForm({ id }) {
  const history = useHistory();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);

  useEffect(() => {
    billingCategoryListService()
      .then((response) => setCategories(response.data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (id) {
      billingItemByIdService(id)
        .then((response) => {
          const item = response.data.item;
          if (!item) return;
          const categoryId =
            item.category && item.category._id ? item.category._id : item.category || undefined;
          setCurrentCategoryId(categoryId || null);
          form.setFieldsValue({ ...item, category: categoryId });
        })
        .catch((error) => handleApiError(error));
    } else {
      form.setFieldsValue({
        product_type: "SERVICE",
        is_taxable: true,
        is_active: true,
      });
    }
  }, [id, form]);

  const onFinish = (values) => {
    setIsLoading(true);
    const payload = {
      ...values,
      category: values.category || null,
      unit: values.unit || "",
      regular_price: Number(values.regular_price) || 0,
      sale_price:
        values.sale_price === "" || values.sale_price === undefined
          ? undefined
          : Number(values.sale_price),
    };
    const action = id
      ? billingItemUpdateService(id, payload)
      : billingItemCreateService(payload);
    action
      .then((response) => {
        setIsLoading(false);
        handleApiSuccess(response);
        history.push("/billing/items");
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  return (
    <Card style={{ maxWidth: 720 }}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Item Name"
          name="product_name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="Part or service name" />
        </Form.Item>
        <Form.Item
          name="product_type"
          label="Type"
          rules={[{ required: true, message: "Please choose a type" }]}
        >
          <Radio.Group>
            <Radio value="PHYSICAL_GOOD">Physical Good</Radio>
            <Radio value="SERVICE">Service</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label="Category"
          name="category"
          help="Group this part or service, for example Phones or Tech Support"
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            placeholder="Select a category"
          >
            {categories
              .filter(
                (c) => c.is_active !== false || c._id === currentCategoryId
              )
              .map((c) => (
                <Option key={c._id} value={c._id}>
                  {c.name}
                  {c.is_active === false ? " (inactive)" : ""}
                </Option>
              ))}
          </Select>
        </Form.Item>
        {categories.length === 0 && (
          <div style={{ marginTop: -8, marginBottom: 16 }}>
            No categories yet. <Link to="/billing/categories/create">Add one</Link>{" "}
            such as Phones, Tech Support, Websites, or Parts.
          </div>
        )}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="SKU" name="sku">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Unit"
              name="unit"
              help="Pick Monthly, Weekly, Yearly, or Per Device, or type your own"
            >
              <UnitInput />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Regular Price"
              name="regular_price"
              rules={[{ required: true, message: "Price is required" }]}
            >
              <InputNumber min={0} step={0.01} prefix="$" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Sale Price" name="sale_price">
              <InputNumber min={0} step={0.01} prefix="$" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Tax should apply?"
              name="is_taxable"
              valuePropName="checked"
              help="When off, this item is never taxed"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Default Tax %" name="default_tax_rate_percentage">
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Default Discount %" name="default_discount_rate_percentage">
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          {id ? "Update Item" : "Create Item"}
        </Button>
      </Form>
    </Card>
  );
}
